import { logUsage } from 'owa-analytics';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { ControlIcons } from 'owa-control-icons';
import EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { lazyUpdateUpsellUserSelectionText } from 'owa-insert-link';
import loc from 'owa-localize';
import {
    uploadAndShareFromCloudMenuText,
    cloudLocationsMenuText,
} from 'owa-attachment-filepicker/lib/index.locstring.json';
import { InsertLinkFromFilePickerContext } from '../types/InsertLinkFromFilePickerContext';
import { checkUserSelectionText } from './checkUserSelectionText';

export enum AttachButton {
    ONLINE_LOCATION_KEY = 'addOnlineLocationAttachment',
    UPLOAD_AND_SHARE_FROM_CLOUD_KEY = 'addUploadAndShareFromCloud',
}

const styles = require('../components/HyperlinkContextMenu.scss');
const classNames = require('classnames');

type AttachmentMultiMenuMap<T> = { onlineLocation: T; uploadAndShareFromCloud: T };

/**
 * To add a button, update the AttachButton and AttachmentMultiMenuMap, then update this method.
 */
export function getAttachmentCommandItems(
    filePickerContext: InsertLinkFromFilePickerContext,
    editorViewState: EditorViewState,
    targetWindow: Window,
    sxsId: string
): IContextualMenuItem[] {
    const attachmentMultiMenuInfo: AttachmentMultiMenuMap<{
        actionName: string;
        onClick: () => void;
    }> = {
        onlineLocation: {
            actionName: 'onlineAttach',
            onClick: () => {
                logUsage('HyperlinkContextMenu', { source: 'CloudLocations' });
                filePickerContext.openFilePickerHandler(
                    filePickerContext.insertLinksHost,
                    editorViewState,
                    targetWindow,
                    sxsId,
                    filePickerContext.fromAddress
                );
            },
        },
        uploadAndShareFromCloud: {
            actionName: 'computerToCloudAttach',
            onClick: async () => {
                logUsage('HyperlinkContextMenu', { source: 'Upsell' });
                // we need to check user selection
                const { shouldContinue, selectionText } = await checkUserSelectionText(
                    editorViewState
                );
                if (!shouldContinue) {
                    return;
                }

                // save the selectionText in order to verify that user can only pick one file when user has selected some text.
                const updateUpsellUserSelectionText = await lazyUpdateUpsellUserSelectionText.import();
                updateUpsellUserSelectionText(selectionText);

                filePickerContext.upsellClickHandler();
            },
        },
    };

    const defaultButtonTypes: AttachButton[] = [
        AttachButton.ONLINE_LOCATION_KEY,
        AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY,
    ];
    const defaultButtons: IContextualMenuItem[] = [];
    defaultButtonTypes.forEach(defaultButtonType => {
        switch (defaultButtonType) {
            case AttachButton.ONLINE_LOCATION_KEY:
                defaultButtons.push(
                    createAttachmentContextMenuItem(
                        loc(cloudLocationsMenuText),
                        AttachButton.ONLINE_LOCATION_KEY,
                        attachmentMultiMenuInfo.onlineLocation.actionName,
                        ControlIcons.Cloud,
                        attachmentMultiMenuInfo.onlineLocation.onClick
                    )
                );
                break;
            case AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY:
                defaultButtons.push(
                    createAttachmentContextMenuItem(
                        loc(uploadAndShareFromCloudMenuText),
                        AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY,
                        attachmentMultiMenuInfo.uploadAndShareFromCloud.actionName,
                        ControlIcons.CloudUpload,
                        attachmentMultiMenuInfo.uploadAndShareFromCloud.onClick
                    )
                );
                break;
            default:
                break;
        }
    });

    return defaultButtons;
}

function createAttachmentContextMenuItem(
    menuItemName: string,
    attachButtonType: AttachButton,
    actionName: string,
    iconName: string,
    onClick: () => void
): IContextualMenuItem {
    return {
        key: attachButtonType,
        name: menuItemName,
        iconProps: {
            iconName: iconName,
            styles: { root: classNames(styles.iconRoot, styles.iconPadding) },
        },
        onClick: onClick,
    };
}
